<template>
    <DefineWeekDetails v-slot="{ week, dialog }">
        <div class="space-y-4" :class="dialog ? 'body text-(--tonal-core-ink)' : 'body-small'">
            <div class="space-y-1">
                <p v-if="!dialog" class="details opacity-80">{{ formatWeekRange(week) }}</p>
                <p v-if="week.upcoming && week.committed" class="body-small-semibold">{{ commitmentLabel(week.committed) }}</p>
                <p v-else-if="week.committed" class="body-small-semibold">{{ completionLabel(week, week.current) }}</p>
            </div>
            <div class="space-y-3 border-t border-current/20 pt-3">
                <p class="body-small-semibold">{{ t("developmentJourneys.goalProgress.activeGoals") }}</p>
                <ul v-if="activeGoals(week).length" class="space-y-3 pl-6" :aria-label="t('developmentJourneys.goalProgress.activeGoals')">
                    <li v-for="goal in activeGoals(week)" :key="goal.id" class="flex items-start gap-2.5">
                        <span class="mt-2 size-2 shrink-0 rounded-full bg-(--tonal-teal-base)" aria-hidden="true"></span>
                        <div class="min-w-0 flex-1 space-y-1">
                            <p :class="dialog ? 'body-semibold text-pretty' : 'body-small-semibold text-pretty'">{{ goal.title }}</p>
                            <p v-if="goal.committed" :class="dialog ? 'body-small text-(--tonal-neutral-600)' : 'details opacity-80'">
                                {{ week.upcoming ? commitmentLabel(goal.committed) : goalCompletionLabel(goal) }}
                            </p>
                        </div>
                    </li>
                </ul>
                <p v-else :class="dialog ? 'body-small text-(--tonal-neutral-600)' : 'details opacity-80'">{{ t("developmentJourneys.goalProgress.noActiveGoals") }}</p>
                <template v-if="closedGoalActions(week).length">
                    <p class="body-small-semibold">{{ t("developmentJourneys.goalProgress.closedGoalActions") }}</p>
                    <ul class="space-y-2" :aria-label="t('developmentJourneys.goalProgress.closedGoalActions')">
                        <li v-for="goal in closedGoalActions(week)" :key="goal.id" class="body-small">
                            {{
                                t("developmentJourneys.goalProgress.goalActions", {
                                    title: goal.title,
                                    activity: week.upcoming ? commitmentLabel(goal.committed) : goalCompletionLabel(goal),
                                })
                            }}
                        </li>
                    </ul>
                </template>
                <p v-if="standaloneActions(week)" class="body-small">
                    {{
                        t("developmentJourneys.goalProgress.otherActions", {
                            activity: week.upcoming ? commitmentLabel(standaloneActions(week)!.committed) : goalCompletionLabel(standaloneActions(week)!),
                        })
                    }}
                </p>
            </div>
            <ul v-if="week.milestones.length" class="space-y-4 border-t border-current/20 pt-3" :aria-label="t('developmentJourneys.goalProgress.goalMilestones')">
                <li v-for="group in milestoneGroups(week)" :key="group.kind" class="space-y-2">
                    <div class="flex items-start gap-2.5">
                        <i class="bi mt-1 w-4 shrink-0" :class="milestoneIconClasses[group.kind]" aria-hidden="true"></i>
                        <p class="body-small-semibold">{{ milestoneHeading(group.kind, group.milestones.length) }}</p>
                    </div>
                    <ul class="space-y-2 pl-6">
                        <li v-for="milestone in group.milestones" :key="milestone.goal_id" class="flex items-start gap-2.5">
                            <span class="mt-2 size-2 shrink-0 rounded-full bg-(--tonal-teal-base)" aria-hidden="true"></span>
                            <span :class="dialog ? 'body text-pretty' : 'body-small text-pretty'">{{ milestone.goal_title }}</span>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    </DefineWeekDetails>
    <BaseCard as="section" radius="lgr" class="@container" :class="{ 'touch-timeline': useWeekDialog }" :aria-label="t('developmentJourneys.goalProgress.followThrough')">
        <div class="flex flex-wrap items-start justify-between gap-3">
            <h3 class="title-body text-(--tonal-core-ink)">{{ t("developmentJourneys.goalProgress.followThrough") }}</h3>
        </div>

        <div class="week-groups mt-5 grid gap-4" :class="{ 'four-quarters': quarters.length === 4 }">
            <div
                v-for="quarter in quarters"
                :key="quarter.start"
                class="quarter-group min-w-0 space-y-3 rounded-lg border border-transparent p-3"
                :class="{ 'current-quarter': quarter.current }">
                <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <p class="body-small text-(--tonal-neutral-600)" :class="{ 'body-small-semibold text-(--tonal-core-ink)': quarter.current }">{{ quarter.label }}</p>
                    <span v-if="quarter.current" class="details text-(--tonal-neutral-600)">{{ t("developmentJourneys.goalProgress.currentQuarter") }}</span>
                </div>
                <ol class="week-grid grid gap-2" :aria-label="quarter.label" :style="{ '--week-columns': quarter.weeks.length }">
                    <li v-for="week in quarter.weeks" :key="week.start" class="min-w-0">
                        <span
                            v-if="week.upcoming && !week.committed && !week.milestones.length"
                            class="week-tile upcoming-week relative block aspect-square w-full rounded-sm"
                            role="img"
                            :aria-label="weekLabel(week)">
                            <span
                                v-if="showWeekNumbers"
                                class="scaffold-body-small-medium absolute inset-0 grid place-items-center text-(--tonal-neutral-600) opacity-60 tabular-nums"
                                aria-hidden="true">
                                {{ week.number }}
                            </span>
                        </span>
                        <BaseDialog v-else width="narrow" :title="formatWeekRange(week)" show-close mobile-height="expanded" @update:open="trackWeekModal(week, $event)">
                            <template #trigger>
                                <GoalProgressWeekTooltip :disabled="useWeekDialog" @update:open="trackWeekTooltip(week, $event)">
                                    <template #trigger>
                                        <BaseButton
                                            theme="tertiary"
                                            :aria-label="weekLabel(week)"
                                            :aria-current="week.current ? 'date' : undefined"
                                            class="week-tile relative block aspect-square w-full min-h-0 rounded-sm border-0 p-0"
                                            :class="milestoneClasses(week)">
                                            <span
                                                class="flex size-full flex-col overflow-hidden rounded-sm"
                                                :class="week.upcoming ? 'upcoming-week' : 'bg-(--tonal-neutral-100)'"
                                                aria-hidden="true">
                                                <template v-if="week.upcoming">
                                                    <span class="flex-1"></span>
                                                    <span class="flex-1" :class="week.committed ? densityClass(week.committed) : ''"></span>
                                                </template>
                                                <template v-else>
                                                    <span class="flex-1" :class="densityClass(week.completed)"></span>
                                                    <span class="flex-1" :class="densityClass(week.committed)"></span>
                                                </template>
                                            </span>
                                            <span
                                                v-if="showWeekNumbers"
                                                class="scaffold-body-small-medium pointer-events-none absolute inset-0 grid place-items-center tabular-nums"
                                                :class="week.upcoming ? 'text-(--tonal-neutral-600) opacity-60' : 'text-(--tonal-core-ink)'"
                                                aria-hidden="true">
                                                {{ week.number }}
                                            </span>
                                        </BaseButton>
                                    </template>
                                    <template #content>
                                        <div class="px-1 py-2" :class="{ 'w-72 max-w-full': week.goals.length || week.milestones.length }">
                                            <WeekDetails :week="week" :dialog="false" />
                                        </div>
                                    </template>
                                </GoalProgressWeekTooltip>
                            </template>
                            <WeekDetails :week="week" :dialog="true" />
                        </BaseDialog>
                    </li>
                </ol>
            </div>
        </div>
        <ul class="legend details-big mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-(--tonal-neutral-600)" :aria-label="t('developmentJourneys.goalProgress.legend')">
            <li v-if="legend.committed" class="flex items-center gap-2">
                <span class="flex size-3 shrink-0 flex-col overflow-hidden rounded-sm bg-(--tonal-neutral-100)" aria-hidden="true">
                    <span class="flex-1"></span><span class="flex-1 bg-(--tonal-teal-base)"></span>
                </span>
                {{ t("developmentJourneys.goalProgress.actionCommitment") }}
            </li>
            <li v-if="legend.completed" class="flex items-center gap-2">
                <span class="flex size-3 shrink-0 flex-col overflow-hidden rounded-sm bg-(--tonal-neutral-100)" aria-hidden="true">
                    <span class="flex-1 bg-(--tonal-teal-base)"></span><span class="flex-1"></span>
                </span>
                {{ t("developmentJourneys.goalProgress.actionCompletion") }}
            </li>
            <li v-if="legend.current" class="flex items-center gap-2">
                <span class="current-week size-3 shrink-0 rounded-sm" aria-hidden="true"></span>
                {{ t("developmentJourneys.goalProgress.thisWeek") }}
            </li>
            <li v-if="legend.moment" class="flex items-center gap-2">
                <span class="milestone-week size-3 shrink-0 rounded-sm" aria-hidden="true"></span>
                {{ t("developmentJourneys.goalProgress.goalMoment") }}
            </li>
            <li v-if="legend.achieved" class="flex items-center gap-2">
                <span class="milestone-week milestone-achieved size-3 shrink-0 rounded-sm" aria-hidden="true"></span>
                {{ t("developmentJourneys.goalProgress.goalAchieved") }}
            </li>
        </ul>
        <slot />
    </BaseCard>
</template>

<script setup lang="ts">
import { createReusableTemplate, useEventListener, useMediaQuery, useTimeoutFn } from "@vueuse/core";
import type { DevelopmentGoal, GoalMoment } from "~vue/api/generated/development-journeys";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import {
    GOAL_PROGRESS_VIEW_METHOD,
    GOAL_PROGRESS_WEEK_CONTEXT,
    type GoalProgressViewMethod,
    logGoalProgressViewed,
} from "~vue/pages/developmentJourneys/progressInsights/goalProgressAnalytics";
import GoalProgressWeekTooltip from "~vue/pages/developmentJourneys/progressInsights/GoalProgressWeekTooltip.vue";
import { actionIsCommitted, actionIsCompleted, localDate } from "~vue/pages/developmentJourneys/progressInsights/overviewMetrics";
import { type ActivityWeek, type CalendarWeek, progressCalendar } from "~vue/pages/developmentJourneys/progressInsights/progressCalendar";
import type { WeeklyAction } from "~vue/pages/developmentJourneys/weeklyActions";
import { formatDate as localizedDate, formatDateRange } from "~vue/utils/formatDate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        goals: DevelopmentGoal[];
        actions: WeeklyAction[];
        goalMoments: GoalMoment[];
        asOfDate: string;
        timezone?: string;
        historyStartDate?: string | null;
    }>(),
    { timezone: "UTC" },
);
type GoalMilestoneKind = GoalMoment["kind"];
const milestoneOrder: GoalMilestoneKind[] = ["achieved", "started", "stopped", "target"];
const milestoneIconClasses: Record<GoalMilestoneKind, string> = {
    started: "bi-flag text-(--tonal-teal-base)",
    achieved: "bi-trophy milestone-achieved",
    stopped: "bi-stop-circle opacity-80",
    target: "bi-bullseye text-(--tonal-teal-base)",
};
// Locale catalogue can finish loading after mount, so these labels must stay reactive to the active locale.
const milestoneTitles = computed<Record<GoalMilestoneKind, string>>(() => ({
    started: t("developmentJourneys.goalProgress.milestoneStarted"),
    achieved: t("developmentJourneys.goalProgress.goalAchieved"),
    stopped: t("developmentJourneys.goalProgress.milestoneStopped"),
    target: t("developmentJourneys.goalProgress.milestoneTarget"),
}));
const milestones = computed(() => props.goalMoments);
interface WeekGoalActivity {
    id: string;
    title: string;
    active: boolean;
    committed: number;
    completed: number;
}
const goalsForWeek = (week: CalendarWeek): WeekGoalActivity[] => {
    const actions = props.actions.filter((action) => action.start_date >= week.start && action.start_date <= week.end && actionIsCommitted(action, props.asOfDate, props.timezone));
    const goalActivity = props.goals.flatMap((goal) => {
        const goalActions = actions.filter((action) => action.development_goal?.id === goal.id);
        const started = localDate(goal.created_at, props.timezone);
        const ended = goal.status === "in_progress" ? null : localDate(goal.status_changed_at, props.timezone);
        const wasActive = started <= props.asOfDate && started <= week.end && (ended === null || ended >= week.start);
        if (!wasActive && !goalActions.length) {
            return [];
        }
        return [
            {
                id: goal.id,
                title: goal.title,
                active: wasActive,
                committed: goalActions.length,
                completed: goalActions.filter((action) => actionIsCompleted(action, props.asOfDate, props.timezone)).length,
            },
        ];
    });
    const standaloneActions = actions.filter((action) => action.development_goal === null);
    const standaloneActivity: WeekGoalActivity[] = standaloneActions.length
        ? [
              {
                  id: "standalone",
                  title: t("developmentJourneys.goalProgress.otherActionsLabel"),
                  active: false,
                  committed: standaloneActions.length,
                  completed: standaloneActions.filter((action) => actionIsCompleted(action, props.asOfDate, props.timezone)).length,
              },
          ]
        : [];
    return [...goalActivity, ...standaloneActivity];
};
const quarters = computed(() =>
    progressCalendar(
        props.actions,
        props.asOfDate,
        [...(props.historyStartDate ? [props.historyStartDate] : []), ...props.goals.map((goal) => localDate(goal.created_at, props.timezone))],
        props.timezone,
    ).map((quarter) => ({
        ...quarter,
        weeks: quarter.weeks.map((week, index) => ({
            ...week,
            number: index + 1,
            goals: goalsForWeek(week),
            milestones: milestones.value
                .filter((milestone) => milestone.date >= week.start && milestone.date <= week.end)
                .sort((a, b) => milestoneOrder.indexOf(a.kind) - milestoneOrder.indexOf(b.kind)),
        })),
    })),
);
type ProgressWeek = (typeof quarters.value)[number]["weeks"][number];
const activeGoals = (week: ProgressWeek): WeekGoalActivity[] => week.goals.filter((goal) => goal.active);
const closedGoalActions = (week: ProgressWeek): WeekGoalActivity[] => week.goals.filter((goal) => goal.id !== "standalone" && !goal.active && goal.committed);
const standaloneActions = (week: ProgressWeek): WeekGoalActivity | undefined => week.goals.find((goal) => goal.id === "standalone");
const milestoneGroups = (week: ProgressWeek): { kind: GoalMilestoneKind; milestones: GoalMoment[] }[] =>
    milestoneOrder.flatMap((kind) => {
        const matching = week.milestones.filter((milestone) => milestone.kind === kind);
        return matching.length ? [{ kind, milestones: matching }] : [];
    });
const milestoneHeading = (kind: GoalMilestoneKind, count: number): string => {
    if (count > 1 && kind === "achieved") {
        return t("developmentJourneys.goalProgress.goalsAchievedPlural");
    }
    if (count > 1 && kind === "started") {
        return t("developmentJourneys.goalProgress.startedTheseGoals");
    }
    return milestoneTitles.value[kind];
};
const [DefineWeekDetails, WeekDetails] = createReusableTemplate<{ week: ProgressWeek; dialog: boolean }>();
const showWeekNumbers = computed(() => quarters.value.length === 1);
const weekAchieved = (week: ProgressWeek): boolean => week.milestones.some((milestone) => milestone.kind === "achieved");
const legend = computed(() => {
    const weeks = quarters.value.flatMap((quarter) => quarter.weeks);
    return {
        committed: weeks.some((week) => week.committed > 0),
        completed: weeks.some((week) => week.completed > 0),
        current: weeks.some((week) => week.current),
        moment: weeks.some((week) => week.milestones.length > 0 && !weekAchieved(week)),
        achieved: weeks.some(weekAchieved),
    };
});
const useWeekDialog = useMediaQuery("(max-width: 767.98px), (pointer: coarse)");
const viewedWeeks = new Set<string>();
let hoveredWeek: ProgressWeek | null = null;
const trackWeekViewed = (week: ProgressWeek, method: GoalProgressViewMethod) => {
    void logGoalProgressViewed({
        goal_progress_view_method: method,
        week_context: week.current ? GOAL_PROGRESS_WEEK_CONTEXT.CURRENT : week.upcoming ? GOAL_PROGRESS_WEEK_CONTEXT.FUTURE : GOAL_PROGRESS_WEEK_CONTEXT.PAST,
        week_action_commitment_count: week.committed,
        week_action_completion_count: week.completed,
        week_goal_count: new Set([...week.goals.filter((goal) => goal.id !== "standalone").map((goal) => goal.id), ...week.milestones.map((milestone) => milestone.goal_id)]).size,
        week_goal_moment_count: week.milestones.length,
        week_goal_moment_types: week.milestones.map((milestone) => milestone.kind),
    });
};
const { start: startHoverTimer, stop: stopHoverTimer } = useTimeoutFn(
    () => {
        if (hoveredWeek && !document.hidden && !useWeekDialog.value) {
            viewedWeeks.add(hoveredWeek.start);
            trackWeekViewed(hoveredWeek, GOAL_PROGRESS_VIEW_METHOD.TOOLTIP);
        }
    },
    5000,
    { immediate: false },
);
const cancelWeekHover = () => {
    stopHoverTimer();
    hoveredWeek = null;
};
const trackWeekTooltip = (week: ProgressWeek, open: boolean | undefined) => {
    if (!open) {
        if (hoveredWeek?.start === week.start) {
            cancelWeekHover();
        }
        return;
    }
    cancelWeekHover();
    if (useWeekDialog.value || document.hidden || viewedWeeks.has(week.start)) {
        return;
    }
    hoveredWeek = week;
    startHoverTimer();
};
const trackWeekModal = (week: ProgressWeek, open: boolean) => {
    if (open) {
        cancelWeekHover();
        trackWeekViewed(week, GOAL_PROGRESS_VIEW_METHOD.MODAL);
    }
};
useEventListener(document, "visibilitychange", cancelWeekHover);
const milestoneClasses = (week: ProgressWeek) => ({
    "current-week": week.current,
    "milestone-week": week.milestones.length > 0,
    "milestone-achieved": week.milestones.some((milestone) => milestone.kind === "achieved"),
});
const formatDate = (date: string): string => localizedDate(date, "date");
const formatWeekRange = (week: ActivityWeek): string => formatDateRange(week.start, week.end, "date");
const completionLabel = (week: ActivityWeek, current = false): string =>
    t("developmentJourneys.goalProgress.completion", { completed: week.completed, committed: week.committed, current: String(current) });
const commitmentLabel = (count: number): string => t("developmentJourneys.goalProgress.commitmentLabel", { count });
const goalCompletionLabel = (goal: WeekGoalActivity): string => t("developmentJourneys.goalProgress.goalCompletionLabel", { completed: goal.completed, committed: goal.committed });
const goalActivityLabel = (goal: WeekGoalActivity, upcoming: boolean): string => {
    if (!goal.committed) {
        return goal.title;
    }
    const activity = upcoming ? commitmentLabel(goal.committed) : goalCompletionLabel(goal);
    return t("developmentJourneys.goalProgress.goalActivityLabel", { title: goal.title, activity });
};
const milestoneLabel = (milestone: GoalMoment): string => {
    const goal = `“${milestone.goal_title}”`;
    const date = formatDate(milestone.date);
    switch (milestone.kind) {
        case "started":
            return t("developmentJourneys.goalProgress.milestoneStartedLabel", { goal, date });
        case "achieved":
            return t("developmentJourneys.goalProgress.milestoneAchievedLabel", { goal, date });
        case "stopped":
            return t("developmentJourneys.goalProgress.milestoneStoppedLabel", { goal, date });
        case "target":
            return t("developmentJourneys.goalProgress.milestoneTargetLabel", { goal, date });
    }
};
const weekLabel = (week: ProgressWeek): string => {
    const weekNumber = showWeekNumbers.value ? t("developmentJourneys.goalProgress.weekNumberSuffix", { number: week.number }) : "";
    const date = t("developmentJourneys.goalProgress.dateRangeWithWeek", { start: formatDate(week.start), end: formatDate(week.end), weekNumber });
    const details: string[] = [];
    if (week.upcoming) {
        details.push(
            week.committed
                ? t("developmentJourneys.goalProgress.upcomingWeekWithCommitment", { commitment: commitmentLabel(week.committed) })
                : t("developmentJourneys.goalProgress.upcomingWeek"),
        );
    } else if (week.committed) {
        details.push(
            week.current
                ? t("developmentJourneys.goalProgress.completionCurrentWeek", { completion: completionLabel(week) })
                : t("developmentJourneys.goalProgress.completionPastWeek", { completion: completionLabel(week) }),
        );
    }
    const goals = activeGoals(week);
    if (goals.length) {
        const labels = goals.map((goal) => goalActivityLabel(goal, week.upcoming)).join("; ");
        details.push(t("developmentJourneys.goalProgress.activeGoalsSummary", { labels }));
    }
    const closedGoals = closedGoalActions(week);
    if (closedGoals.length) {
        const labels = closedGoals.map((goal) => goalActivityLabel(goal, week.upcoming)).join("; ");
        details.push(t("developmentJourneys.goalProgress.closedGoalActionsSummary", { labels }));
    }
    const otherActions = standaloneActions(week);
    if (otherActions) {
        const activity = week.upcoming ? commitmentLabel(otherActions.committed) : goalCompletionLabel(otherActions);
        details.push(t("developmentJourneys.goalProgress.otherActionsSummary", { activity }));
    }
    if (week.milestones.length) {
        details.push(week.milestones.map(milestoneLabel).join(" "));
    }
    return details.length ? `${date}: ${details.join(" ")}` : date;
};
const densityClass = (count: number): string => {
    if (count === 0) {
        return "bg-(--tonal-neutral-100)";
    }
    const density = ["opacity-40", "opacity-60", "opacity-80"][count - 1] ?? "opacity-100";
    return `bg-(--tonal-teal-base) ${density}`;
};
</script>

<style scoped>
@layer components {
    .week-grid {
        grid-template-columns: repeat(var(--week-columns), minmax(0, 1fr));
    }

    .week-tile {
        max-width: 3rem;
    }

    .week-tile:focus-visible::after {
        content: "";
        position: absolute;
        inset: 3px;
        border: 2px solid var(--tonal-core-ink);
        border-radius: inherit;
        pointer-events: none;
    }

    .current-quarter {
        border-color: color-mix(in srgb, var(--tonal-teal-base) 35%, var(--tonal-neutral-200));
    }

    .current-week {
        outline: 2px dashed var(--tonal-core-ink);
        outline-offset: 4px;
    }

    .legend .current-week {
        outline-offset: 1px;
    }

    .upcoming-week {
        border: 1px solid var(--tonal-neutral-200);
    }

    .milestone-week {
        color: color-mix(in srgb, var(--tonal-teal-base) 55%, var(--tonal-core-ink));
        box-shadow: 0 0 0 2px currentColor;
    }

    .milestone-achieved {
        color: var(--tonal-orange-strong);
    }

    @container (min-width: 36rem) {
        .week-groups.four-quarters {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .four-quarters .week-grid {
            grid-template-columns: repeat(7, minmax(0, 1fr));
            gap: 0.5rem;
        }
        .four-quarters .week-tile {
            max-width: 2rem;
        }
    }

    .touch-timeline .week-grid {
        grid-template-columns: repeat(auto-fill, minmax(2.75rem, 1fr));
        gap: 0.5rem;
    }

    .touch-timeline .week-tile {
        max-width: 3rem;
    }
}
</style>
